import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from '@/components/ui/sonner';
import {
  getGalleryBySlug,
  unlockGallery,
  getClientOwnedGallery,
  getMyFavorites,
  toggleFavorite,
  downloadImage,
  downloadGalleryZip,
  getMySelections,
  toggleSelection,
  getGalleryComments,
  addGalleryComment,
  apiErrorMessage,
} from '@/lib/api';
import { triggerBlobDownload } from '@/lib/download';

const toggleInSet = (set, id) => {
  const next = new Set(set);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
};

/**
 * Everything a gallery page needs to know about one gallery: the category
 * (public, unlocked, or client-owned), plus the client-only interactions
 * (favorites, final picks, comments, downloads). Optimistic updates revert on
 * failure so the UI always reflects the server.
 */
export const useClientGallery = (slug, ownsGallery) => {
  const [category, setCategory] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [favorites, setFavorites] = useState(() => new Set());
  const [selections, setSelections] = useState(() => new Set());
  const [comments, setComments] = useState({});

  useEffect(() => {
    let cancelled = false;
    setCategory(null);
    setNotFound(false);

    const fetcher = ownsGallery ? getClientOwnedGallery(slug) : getGalleryBySlug(slug);
    fetcher.then((d) => { if (!cancelled) setCategory(d); }).catch(() => { if (!cancelled) setNotFound(true); });

    if (ownsGallery) {
      getMyFavorites(slug).then((d) => { if (!cancelled) setFavorites(new Set(d[slug] || [])); }).catch(() => { if (!cancelled) setFavorites(new Set()); });
      getMySelections(slug).then((d) => { if (!cancelled) setSelections(new Set(d[slug] || [])); }).catch(() => { if (!cancelled) setSelections(new Set()); });
      getGalleryComments(slug).then((d) => { if (!cancelled) setComments(d); }).catch(() => { if (!cancelled) setComments({}); });
    } else {
      setFavorites(new Set());
      setSelections(new Set());
      setComments({});
    }
    return () => { cancelled = true; };
  }, [slug, ownsGallery]);

  const unlock = useCallback(async (accessCode) => {
    const unlocked = await unlockGallery(slug, accessCode);
    setCategory(unlocked);
    return unlocked;
  }, [slug]);

  const toggleFavoriteFor = useCallback(async (image) => {
    setFavorites((prev) => toggleInSet(prev, image.id));
    try {
      const res = await toggleFavorite(slug, image.id);
      toast.success(res.favorited ? 'Added to your favorites.' : 'Removed from favorites.');
    } catch (err) {
      setFavorites((prev) => toggleInSet(prev, image.id)); // revert
      toast.error('Could not update favorites. Please try again.');
    }
  }, [slug]);

  const toggleSelectionFor = useCallback(async (image) => {
    setSelections((prev) => toggleInSet(prev, image.id));
    try {
      const res = await toggleSelection(slug, image.id);
      toast.success(res.selected ? 'Added to your final picks.' : 'Removed from your final picks.');
    } catch (err) {
      setSelections((prev) => toggleInSet(prev, image.id)); // revert
      if (err?.response?.status === 409) toast.error(apiErrorMessage(err, "You've reached your selection limit."));
      else toast.error('Could not update your selection. Please try again.');
    }
  }, [slug]);

  const addComment = useCallback(async (image, body, isRevisionRequest) => {
    try {
      const comment = await addGalleryComment(slug, image.id, body, isRevisionRequest);
      setComments((prev) => ({ ...prev, [image.id]: [...(prev[image.id] || []), comment] }));
      toast.success(isRevisionRequest ? 'Revision request sent to your photographer.' : 'Note sent to your photographer.');
    } catch (err) {
      toast.error('Could not send your note. Please try again.');
    }
  }, [slug]);

  const download = useCallback(async (image, resolution = 'original') => {
    try {
      const { blob, filename } = await downloadImage(slug, image.id, resolution);
      triggerBlobDownload(blob, filename);
      toast.success(`Downloading ${filename}`);
    } catch (err) {
      toast.error('Download failed. Please try again or contact the studio.');
    }
  }, [slug]);

  const downloadZip = useCallback(async (scope, resolution) => {
    const { blob, filename } = await downloadGalleryZip(slug, scope, resolution);
    triggerBlobDownload(blob, filename);
    toast.success(`Downloading ${filename}`);
  }, [slug]);

  const isLocked = Boolean(category?.is_private && category.images.length === 0 && !ownsGallery);
  const selectionLimit = category?.selection_limit ?? null;
  const images = useMemo(() => category?.images || [], [category]);

  return {
    category, notFound, isLocked, selectionLimit, images,
    favorites, selections, comments,
    unlock, toggleFavoriteFor, toggleSelectionFor, addComment, download, downloadZip,
  };
};
